<template>
  <InfuseField :label="t('cpms.firmware.selectFirmware')" class="w-64" size="small">
    <InfuseSelect
      v-model="selectedFirmwareId"
      :options="firmwareOptions"
      :loading="isLoading"
      :placeholder="t('cpms.firmware.selectFirmwarePlaceholder')"
      class="w-full"
      size="small"
      name="firmware"
    />
  </InfuseField>
</template>

<script lang="ts" setup>
import { Firmware } from '@infuse/api/cpms/firmware-management';
import { useSearchFirmware } from '@infuse/cpms/firmware-management/queries';
import { InfuseField, InfuseSelect } from '@infuse/ui/components';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Props {
  modelValue?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined
});

interface Emits {
  (e: 'update:modelValue', value?: string): void;
  (e: 'firmware-selected', firmware?: Firmware): void;
}

const emit = defineEmits<Emits>();

const selectedFirmwareId = ref<string | undefined>(props.modelValue);

const { firmware, isLoading, isFetching, isFetchNextPageError, hasNextPage, fetchNextPage } = useSearchFirmware();

// A select has no scroll to load more, so every page is fetched up front, one at a time.
watch(
  [hasNextPage, isFetching],
  ([more, fetching]) => {
    if (more && !fetching && !isFetchNextPageError.value) fetchNextPage({ cancelRefetch: false });
  },
  { immediate: true }
);

// Create options for the select dropdown
const firmwareOptions = computed(() =>
  firmware.value.map((fw) => ({
    key: fw.name || fw.filename,
    value: fw.id
  }))
);

// Watch for changes to modelValue prop
watch(
  () => props.modelValue,
  (newValue) => {
    selectedFirmwareId.value = newValue;
  },
  { immediate: true }
);

// Watch for changes to selectedFirmwareId or firmware list loading
watch([selectedFirmwareId, firmware], ([newValue, newFirmware], [oldValue, oldFirmware]) => {
  if (newValue !== oldValue) {
    emit('update:modelValue', newValue);
  }

  // Find the selected firmware object — only emit when found or explicitly cleared
  if (!newValue) {
    emit('firmware-selected', undefined);
  } else {
    const found = firmware.value.find((fw) => fw.id === newValue);
    if (found) {
      emit('firmware-selected', found);
    }
  }
});

// Expose the selected firmware for parent components
const selectedFirmware = computed(() => firmware.value.find((fw) => fw.id === selectedFirmwareId.value));

defineExpose({
  selectedFirmware,
  selectedFirmwareId
});
</script>
